import { memo, useCallback } from "react"
import { formatDistanceToNow } from "date-fns"
import { IconTriangleWarningOutline18 as AlertTriangle } from "nucleo-ui-outline-18"

import type { ChangelogVersion } from "@productlane/zero"
import { Button } from "@productlane/ui"

import { analytics } from "@/lib/analytics"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { useChangelogContext } from "./changelog-context"
import { restoreChangelogVersion } from "./restore-changelog-version"

interface RestorePreviewBannerProps {
	changelogId: string
	version: ChangelogVersion
	defaultLanguage: string
}

export const RestorePreviewBanner = memo(
	({ changelogId, version, defaultLanguage }: RestorePreviewBannerProps) => {
		const { setPreviewVersion, setIsDirty } = useChangelogContext()
		const zero = useZero()
		const workspace = useZeroWorkspace()

		const relativeTime = formatDistanceToNow(version.createdAt, {
			addSuffix: true,
		})

		const handleCancel = useCallback(() => {
			setPreviewVersion(null)
			analytics.track("cancel-version-restore", {
				changelogId,
				versionId: version.id,
			})
		}, [setPreviewVersion, changelogId, version.id])

		const handleConfirm = useCallback(async () => {
			if (!workspace) return
			const restored = await restoreChangelogVersion({
				zero,
				workspaceId: workspace.id,
				changelogId,
				version,
				defaultLanguage,
			})
			if (!restored) return
			setPreviewVersion(null)
			setIsDirty(false)
		}, [
			zero,
			workspace,
			changelogId,
			version,
			defaultLanguage,
			setPreviewVersion,
			setIsDirty,
		])

		return (
			<div className="z-50 flex-shrink-0 border-b border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
				<div className="flex items-center justify-between gap-4 px-6 py-3">
					<div className="flex items-center gap-3">
						<AlertTriangle className="h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-500" />
						<div className="flex flex-col gap-1">
							<p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
								Previewing version from {relativeTime}
							</p>
							<p className="text-xs text-yellow-700 dark:text-yellow-300">
								This is a preview. Your current changes have not been saved yet.
							</p>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="ghost" size="sm" onClick={handleCancel}>
							Cancel
						</Button>
						<Button variant="default" size="sm" onClick={handleConfirm}>
							Confirm Restore
						</Button>
					</div>
				</div>
			</div>
		)
	},
)

RestorePreviewBanner.displayName = "RestorePreviewBanner"
